/**
 * Le thème se choisit, et il se souvient — Clair / Sombre / Système.
 *
 * Ce module est le point UNIQUE où le choix de thème est résolu et appliqué.
 * Avant lui, `useThemePreference` vivait dans `OsShell` : le shell posait
 * `html.os-theme-dark` au montage et le RETIRAIT au démontage, donc tout ce qui
 * n'était pas l'OS (dashboard, facturation, écrans d'auth) restait clair par
 * construction — voir `docs/dark-theme-scope.md` §1. La portée tranchée au §6
 * est « le produit authentifié » : le choix doit donc survivre à la sortie du
 * shell, ce qui veut dire qu'il ne peut plus appartenir au shell.
 *
 * ── Les trois états, et pourquoi ils ne se codent pas pareil ──────────────
 *
 *   'light'  — clair, quoi que dise l'OS.
 *   'dark'   — sombre, quoi que dise l'OS.
 *   'system' — DÉFAUT quand rien n'est stocké : seule `prefers-color-scheme`
 *              tranche, et elle est écoutée en continu (l'OS peut basculer
 *              pendant la session).
 *
 * `choice` est le choix, `isDark` est le RÉSULTAT. Le 404 (§6) suit le choix
 * STOCKÉ et jamais `prefers-color-scheme` : il lit donc `choice === 'dark'`,
 * pas `isDark`. Les deux règles sont différentes, elles sont écrites
 * différemment — c'est le piège que ce module rend impossible à rater.
 *
 * ── Deux stockages, deux rôles (§7) ──────────────────────────────────────
 *
 * Le REGISTRE est la préférence de compte (`/agent/preferences`, `theme`) :
 * portable d'un poste à l'autre, comme le veut DESIGN.md §7. Le CANAL
 * D'AVANT-PEINTURE est `localStorage['os.theme.dark']`, sur le patron de
 * `aikka-lang` (`src/i18n/index.ts`) : un script en ligne dans `index.html` ne
 * peut pas attendre une requête réseau, donc il lit ce cache. Le compte gagne
 * dès qu'il arrive et réécrit le cache. Aucune des deux instructions n'est
 * écartée.
 *
 * La clé de cache garde son nom historique (`os.theme.dark`) et lit encore les
 * anciennes valeurs `"true"` / `"false"` : un opérateur qui avait déjà choisi
 * le sombre ne repart pas de zéro. Elle écrit désormais le choix en toutes
 * lettres, parce qu'un booléen ne sait pas dire « système ».
 *
 * ── Le sol, et pourquoi une surface peut le réclamer clair ────────────────
 *
 * `html.os-theme-dark` est le sélecteur qui atteint TOUT `.os-scope` non
 * explicitement clair, y compris les surfaces portées sur `document.body`
 * (Menu, SettingsModal…). Le site public reste clair par décision (§6) : les
 * surfaces publiques déclarent donc `useLightGround()`, et tant qu'au moins une
 * est montée, la classe racine n'est PAS posée. C'est plus sûr que de compter
 * sur `data-theme="light"` seul : un menu porté sur `document.body` échappe au
 * scope de la page et redeviendrait sombre au-dessus d'une page claire.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentApi, type AgentPreferences, type ThemeChoice } from '@/api/agent';
import { useAuth } from '@/hooks/useAuth';

export type { ThemeChoice };

/** Shared with the other preference hooks so all read one cache entry. */
const PREFERENCES_KEY = ['agent', 'preferences'] as const;

/**
 * Canal d'avant-peinture, PAS le registre. Le nom de la clé est repris tel quel
 * par le script anti-flash d'`index.html` : les deux doivent rester d'accord.
 */
export const THEME_CACHE_KEY = 'os.theme.dark';

/**
 * Barre d'URL mobile / overscroll — les deux sols de `os.css` (`html.os-root`).
 *
 * EXCEPTION couleur en dur, nommée (garde-fou `scripts/check-color-tokens.mjs`) :
 * `<meta name="theme-color">` est le seul endroit où le navigateur peint HORS
 * de la page, et un attribut de méta ne résout pas `var()`. La valeur doit en
 * plus être lisible AVANT la première feuille de style — le script d'amorçage
 * d'`index.html` écrit les deux mêmes octets, et les deux écritures doivent
 * rester d'accord. Ces deux littéraux ne se tokenisent donc pas ; ils se
 * relisent en même temps qu'`index.html`.
 */
const THEME_COLOR_DARK = '#000000';
const THEME_COLOR_LIGHT = '#EFEEEA';

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Le choix EXPLICITEMENT stocké, ou `null` si l'opérateur n'a jamais tranché.
 * `"true"` / `"false"` sont l'ancien format booléen, toujours honoré.
 */
export function readStoredChoice(): ThemeChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(THEME_CACHE_KEY);
    if (raw === 'true') return 'dark';
    if (raw === 'false') return 'light';
    if (isThemeChoice(raw)) return raw;
  } catch {
    /* storage disabled — no stored choice, the OS preference decides */
  }
  return null;
}

/** Rien de stocké ⇒ « système » : `prefers-color-scheme` est ce que l'opérateur a déjà dit à son OS. */
export function readBootChoice(): ThemeChoice {
  return readStoredChoice() ?? 'system';
}

function writeBootCache(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_CACHE_KEY, choice);
  } catch {
    /* storage disabled — the account preference still carries the choice */
  }
}

function prefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/** Le choix → le résultat. « Système » est la règle qui désigne une des deux peaux, pas une troisième. */
export function resolveIsDark(choice: ThemeChoice, systemDark: boolean): boolean {
  return choice === 'dark' || (choice === 'system' && systemDark);
}

interface ThemeContextValue {
  /** Ce que l'opérateur a choisi — 'system' tant qu'il n'a rien tranché. */
  choice: ThemeChoice;
  /** Ce qui est peint. `choice === 'system'` ⇒ suit `prefers-color-scheme`. */
  isDark: boolean;
  /** Pose un choix : compte (registre) + cache d'amorçage, dans cet ordre de vérité. */
  setChoice: (choice: ThemeChoice) => void;
  /** Clair ⇄ sombre en un geste (la lune de la topbar). Fige toujours un choix explicite. */
  toggle: () => void;
  /** Interne — `useLightGround`. Une surface publique retient le sol clair. */
  retainLightGround: (retain: boolean) => void;
  /** Interne — `useOsRoot`. Une surface qui PEINT le sol du document le retient. */
  retainOsRoot: (retain: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const [choice, setChoiceState] = useState<ThemeChoice>(() => readBootChoice());
  const [systemDark, setSystemDark] = useState<boolean>(() => prefersDark());
  const [lightGround, setLightGround] = useState(0);
  // `html.os-root` est COMPTÉ, exactement comme le sol clair l'est déjà — et
  // c'est la correction d'une régression du lot 3, mesurée dans Chromium sur le
  // build de production. Quatre surfaces posaient la classe et la RETIRAIENT au
  // démontage (`OsShell`, `DashboardLayout`, `PublicShell`, `NotFoundPage`) ;
  // `AuthShell` ne la posait jamais et vivait du seul script d'`index.html`.
  // Donc `/store → /login` en sombre : `PublicShell` retirait au démontage une
  // classe qu'il n'avait pas seul, et le `/login` noir se retrouvait sur un
  // `body` CRÈME (mesuré : `rgb(245,244,239)`). Un compteur ferme le motif pour
  // les cinq surfaces au lieu d'ajouter un cas de plus.
  const [osRootClaims, setOsRootClaims] = useState(0);
  // La revendication du script d'amorçage. Il pose `os-root` avant l'hydratation
  // sur les chemins produit ; sans elle le compteur partirait de zéro et la
  // première surface à se démonter arracherait le sol posé avant elle. Elle est
  // CÉDÉE à la première vraie revendication (et pas conservée), pour que la
  // sémantique d'aujourd'hui tienne à l'octet près : sortir du produit vers la
  // landing rend bien le sol.
  const [bootGround, setBootGround] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('os-root'),
  );

  // « Système » n'est pas un instantané : l'OS peut basculer pendant la session.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    setSystemDark(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Le registre (§7). Interrogé seulement une fois connecté : le site public
  // n'a pas de compte à lire et n'a rien à demander à /agent/preferences.
  const query = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => (await agentApi.getPreferences()).data,
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Le compte gagne dès qu'il arrive. Un `theme` absent (ancienne ligne, ou
  // colonne pas encore livrée) laisse le choix d'amorçage en place plutôt que
  // de renvoyer l'opérateur au clair.
  const accountTheme = query.data?.theme;
  useEffect(() => {
    if (!isThemeChoice(accountTheme)) return;
    setChoiceState(accountTheme);
    writeBootCache(accountTheme);
  }, [accountTheme]);

  const mutation = useMutation({
    mutationFn: (theme: ThemeChoice) => agentApi.updatePreferences({ theme }).then((r) => r.data),
    onSuccess: (data: AgentPreferences) => {
      queryClient.setQueryData(PREFERENCES_KEY, data);
    },
  });

  const setChoice = useCallback(
    (next: ThemeChoice) => {
      setChoiceState(next);
      // Le cache d'amorçage d'abord : c'est lui que le prochain chargement lit,
      // et il doit être juste même si le PUT échoue ou si personne n'est connecté.
      writeBootCache(next);
      queryClient.setQueryData<AgentPreferences>(PREFERENCES_KEY, (p) =>
        p ? { ...p, theme: next } : p,
      );
      if (isAuthenticated) mutation.mutate(next);
    },
    [isAuthenticated, mutation, queryClient],
  );

  const isDark = resolveIsDark(choice, systemDark);

  const toggle = useCallback(() => {
    setChoice(isDark ? 'light' : 'dark');
  }, [isDark, setChoice]);

  const retainLightGround = useCallback((retain: boolean) => {
    setLightGround((n) => Math.max(0, n + (retain ? 1 : -1)));
  }, []);

  const retainOsRoot = useCallback((retain: boolean) => {
    if (retain) setBootGround(false);
    setOsRootClaims((n) => Math.max(0, n + (retain ? 1 : -1)));
  }, []);

  // La classe racine. Elle atteint tout `.os-scope` non explicitement clair,
  // portails compris — donc elle n'est PAS posée tant qu'une surface publique
  // retient le sol clair (§6 : le site public reste clair).
  //
  // `useLayoutEffect`, et c'est mesuré, pas préventif : les routes publiques
  // sont paresseuses. Ce provider commite donc AVANT elles, sol sombre, et
  // `PublicShell` n'arrive que ~40 ms plus tard. Avec un effet passif des deux
  // côtés, `os-root` (PublicShell) atterrissait pendant que `os-theme-dark`
  // était encore posé : /store et /pricing clignotaient NOIR pendant ~6 ms.
  // En effet de layout, la reprise du sol clair est résolue avant la peinture
  // du commit où la page publique arrive — il n'y a plus de trame noire.
  const rootDark = isDark && lightGround === 0;
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('os-theme-dark', rootDark);
  }, [rootDark]);

  // Le sol du document. Une seule écriture, depuis le compteur — plus aucune
  // surface ne touche `classList` elle-même.
  const ownsGround = osRootClaims > 0 || bootGround;
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('os-root', ownsGround);
  }, [ownsGround]);

  // `theme-color` est la SEULE couleur que le navigateur peint hors de la page
  // (la barre d'URL mobile), donc la seule qui puisse rester FAUSSE sans que
  // rien à l'écran ne le montre. Deux défauts opposés se croisent ici, et il
  // a fallu les deux passes pour les voir tous les deux :
  //
  //   · PEINDRE TROP TÔT — mesuré au frame près sur le build de production :
  //     /store · /pricing · / passaient la méta à #000000 à t+61 ms (premier
  //     commit de ce provider, avant que la route publique paresseuse n'arrive)
  //     et ne la rendaient à #EFEEEA qu'à t+101 ms — ~40 ms de barre d'URL
  //     NOIRE en tête d'une page qui, elle, n'a jamais noirci.
  //   · NE JAMAIS LA LIBÉRER — le défaut que la première garde a introduit, et
  //     que les quatre tests d'alors manquaient. Trois surfaces publiques
  //     retiennent le sol CLAIR sans revendiquer `os-root` : `LandingPage`,
  //     `SharedRunPage`, et le 404 en clair (`useLightGround(!dark)`). Un
  //     opérateur en sombre qui quitte /agent pour / était donc laissé avec la
  //     méta à #000000 — écrite légitimement quand le shell possédait le sol —
  //     au-dessus d'une landing crème, jusqu'au prochain chargement dur.
  //
  // La règle qui tient les deux : on écrit dès qu'une surface a dit quelque
  // chose du sol, qu'elle le PEIGNE (`ownsGround`) ou qu'elle le retienne CLAIR
  // (`lightGround`). L'abstention ne couvre plus que la fenêtre où personne n'a
  // encore parlé : l'amorçage avant la première surface, et le même trou de
  // Suspense sur chunk froid (`PageLoader` ne revendique rien). Là, et là
  // seulement, la valeur posée par `index.html` reste la bonne — c'est la même
  // liste blanche qui l'a décidée.
  //
  // `rootDark` porte déjà `lightGround === 0` : sur une surface publique la
  // valeur écrite est donc toujours le crème, thème sombre choisi ou non.
  const paintsThemeColor = ownsGround || lightGround > 0;
  useLayoutEffect(() => {
    if (!paintsThemeColor) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', rootDark ? THEME_COLOR_DARK : THEME_COLOR_LIGHT);
  }, [paintsThemeColor, rootDark]);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, isDark, setChoice, toggle, retainLightGround, retainOsRoot }),
    [choice, isDark, setChoice, toggle, retainLightGround, retainOsRoot],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Le thème, partout. Hors provider (un test qui monte un composant nu), il
 * renvoie un clair inerte plutôt que de jeter : aucune surface ne doit refuser
 * de rendre parce que personne ne lui a dit quelle couleur porter.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  return (
    ctx ?? {
      choice: 'light',
      isDark: false,
      setChoice: () => {},
      toggle: () => {},
      retainLightGround: () => {},
      retainOsRoot: () => {},
    }
  );
}

/**
 * « Cette surface est publique : garde le sol clair tant que je suis à l'écran. »
 * Montée par `PublicShell` (groupes marketing), la landing et la page de partage.
 */
export function useLightGround(active = true): void {
  const { retainLightGround } = useTheme();
  // Layout, pas passif : la revendication doit être enregistrée avant que le
  // commit où la surface publique apparaît ne soit peint. Voir la note sur la
  // trame noire de /store dans `ThemeProvider`.
  useLayoutEffect(() => {
    if (!active) return;
    retainLightGround(true);
    return () => retainLightGround(false);
  }, [active, retainLightGround]);
}

/**
 * « Cette surface PEINT le sol du document : garde `html.os-root` tant que je
 * suis à l'écran. »
 *
 * `html.os-root` peint `<html>` et `<body>` (os.css) — l'overscroll, le sol
 * au-delà du scope, et c'est aussi lui qui autorise l'écriture de
 * `theme-color`. Il est COMPTÉ et non booléen, parce qu'à toute navigation
 * côté client deux surfaces se croisent : celle qui part rendait la classe
 * pendant que celle qui arrive la reprenait, et une surface qui ne la
 * revendiquait pas (`AuthShell`) héritait d'un sol arraché. Le compteur rend
 * le croisement inoffensif et la revendication obligatoire.
 */
export function useOsRoot(active = true): void {
  const { retainOsRoot } = useTheme();
  // Layout et pas passif, pour la même raison que `useLightGround` : la reprise
  // doit être résolue avant la peinture du commit où la surface apparaît.
  useLayoutEffect(() => {
    if (!active) return;
    retainOsRoot(true);
    return () => retainOsRoot(false);
  }, [active, retainOsRoot]);
}
